// Combat and ambience: muzzle flashes, weapon tracers, travelling shells with
// smoke trails, impact sparks, shockwave rings, dust plumes, burning wrecks,
// venting geysers and the dust a column of units kicks up as it moves.
//
// Everything here is pooled into fixed-size struct-of-arrays buffers allocated
// once at construction. A frame with 300 units shooting emits a few hundred
// events; if each of those allocated a particle object the GC would show up as
// a stutter exactly when the game is busiest, which is exactly when it must not.
//
// FOUR DRAW CALLS TOTAL:
//   tracerMesh   camera-facing beam quads with a hot core and a falloff
//   spriteMesh   every particle, in ONE instanced quad batch. Additive sparks
//                and alpha-blended smoke share a material by writing
//                premultiplied alpha: an "additive" particle emits colour with
//                zero alpha, a "smoke" particle emits colour x alpha. One
//                program, both blend modes, no sorting problem.
//   ringMesh     ground shockwaves
//   wreckMesh    lit hulks left behind by dead units, which tip over and sink
//
// Randomness uses a local xorshift generator, never Math.random and never
// world.rng -- the renderer must not be able to perturb the simulation's seeded
// stream, or replays would depend on how many frames were drawn.

import * as THREE from 'three';
import { PALETTE, FLY_ALT, MUZZLE, mergeParts, makeSolidMaterial } from './units.js';
import { PLAYER, FOG_VISIBLE } from '../sim/constants.js';
import { UnitDefs } from '../sim/defs.js';

const TRACER_CAP = 256;
const PARTICLE_CAP = 640;
const RING_CAP = 96;
const WRECK_CAP = 28;

const K_FLASH = 0;     // static, shrinks and fades in place
const K_SHOT = 1;      // travelling projectile: interpolates start -> target
const K_SPARK = 2;     // ballistic ember, additive
const K_SMOKE = 3;     // rises, grows, alpha blended
const K_DUST = 4;      // hugs the ground, spreads outward
const K_DEBRIS = 5;    // dark chunk, gravity, no glow

/** Tiny deterministic PRNG so effects never touch world.rng or Math.random. */
function makeRng(seed) {
  let s = seed >>> 0 || 0x9e3779b9;
  return () => {
    s ^= s << 13; s >>>= 0;
    s ^= s >>> 17;
    s ^= s << 5; s >>>= 0;
    return s / 4294967296;
  };
}

// --- tracer: a stretched billboard, bright down the middle ------------------
const TRACER_VS = /* glsl */`
attribute vec3 aCol;
attribute vec2 aBeam;      // x: 0 at muzzle -> 1 at impact, y: -1..1 across
varying vec3 vCol;
varying vec2 vBeam;
void main() {
  vCol = aCol;
  vBeam = aBeam;
  gl_Position = projectionMatrix * viewMatrix * modelMatrix * vec4( position, 1.0 );
}`;

const TRACER_FS = /* glsl */`
varying vec3 vCol;
varying vec2 vBeam;
void main() {
  float across = 1.0 - abs( vBeam.y );
  float core = pow( across, 7.0 );
  float glow = pow( across, 1.8 );
  // hot at the leading end, thinning back toward the muzzle
  float along = 0.22 + 0.78 * vBeam.x;
  vec3 c = vCol * ( glow * 0.85 + core * 2.2 ) * along;
  gl_FragColor = vec4( c, 1.0 );
}`;

// --- particles: one material, additive and alpha in the same batch ---------
const PART_VS = /* glsl */`
attribute vec2 aParam;     // x: opacity, y: additiveness (1 = pure add)
varying vec3 vCol;
varying vec2 vUv;
varying vec2 vP;
void main() {
  vUv = uv;
  vP = aParam;
  vec3 c = vec3( 1.0 );
  #ifdef USE_INSTANCING_COLOR
    c = instanceColor;
  #endif
  vCol = c;
  gl_Position = projectionMatrix * viewMatrix * modelMatrix * instanceMatrix * vec4( position, 1.0 );
}`;

const PART_FS = /* glsl */`
varying vec3 vCol;
varying vec2 vUv;
varying vec2 vP;
void main() {
  float d = length( vUv - 0.5 ) * 2.0;
  float soft = max( 0.0, 1.0 - d );
  float body = pow( soft, mix( 1.9, 0.85, vP.y ) );
  float core = pow( soft, 7.0 ) * vP.y;
  float a = clamp( body * vP.x * ( 1.0 - vP.y ), 0.0, 1.0 );
  vec3 c = vCol * ( body + core * 2.4 ) * vP.x;
  if ( body < 0.004 ) discard;
  gl_FragColor = vec4( c, a );
}`;

// --- shockwave ring: hot leading edge, soft scorched interior --------------
const RING_VS = /* glsl */`
varying vec3 vCol;
varying vec2 vUv;
void main() {
  vUv = uv;
  vec3 c = vec3( 1.0 );
  #ifdef USE_INSTANCING_COLOR
    c = instanceColor;
  #endif
  vCol = c;
  gl_Position = projectionMatrix * viewMatrix * modelMatrix * instanceMatrix * vec4( position, 1.0 );
}`;

const RING_FS = /* glsl */`
varying vec3 vCol;
varying vec2 vUv;
void main() {
  float d = length( vUv - 0.5 ) * 2.0;
  float edge = smoothstep( 0.72, 0.98, d );
  float inner = pow( clamp( ( d - 0.20 ) / 0.80, 0.0, 1.0 ), 2.5 ) * 0.40;
  float a = edge * 1.25 + inner;
  if ( a < 0.004 ) discard;
  gl_FragColor = vec4( vCol * a, 1.0 );
}`;

const WEAPON_COLOR = {
  bullet: [1.0, 0.90, 0.52],
  laser: [0.42, 0.94, 1.0],
  shell: [1.0, 0.72, 0.32],
  arc: [1.0, 0.60, 0.22],
  missile: [1.0, 0.76, 0.42],
};

// (u along beam, v across) for the six beam-quad vertices
const BEAM_UV = new Float32Array([0, -1, 0, 1, 1, 1, 0, -1, 1, 1, 1, -1]);

const SMOKE_COLOR = [0.30, 0.29, 0.28];
const DUST_COLOR = [0.44, 0.39, 0.30];

const _v = new THREE.Vector3();
const _q = new THREE.Quaternion();
const _s = new THREE.Vector3();
const _m = new THREE.Matrix4();
const _e = new THREE.Euler(0, 0, 0, 'YZX');
const _fwd = new THREE.Vector3();
// six vertices of a beam quad, reused every tracer every frame
const _tq = new Float32Array(18);
const _tint = [1, 1, 1];
const _mz = [0, 0, 0];
const _dir = new THREE.Vector3();
const _side = new THREE.Vector3();

/** Charred hulk: what is left of a unit once it has stopped being one. */
function modelWreck() {
  const char = [0.055, 0.050, 0.048];
  const scorch = [0.10, 0.085, 0.075];
  return mergeParts([
    { g: new THREE.BoxGeometry(0.86, 0.30, 0.60), y: 0.15, c: char, t: 1 },
    { g: new THREE.BoxGeometry(0.44, 0.20, 0.52), x: -0.30, y: 0.34, rz: 0.30, c: scorch, t: 1 },
    { g: new THREE.BoxGeometry(0.50, 0.06, 0.46), x: 0.34, y: 0.10, rz: -0.42, c: char, t: 1 },
    { g: new THREE.BoxGeometry(0.10, 0.34, 0.10), x: 0.12, y: 0.36, rz: 0.5, rx: 0.3, c: scorch, t: 1 },
    { g: new THREE.BoxGeometry(0.26, 0.10, 0.22), x: -0.52, y: 0.06, z: 0.30, ry: 0.6, c: char, t: 1 },
    { g: new THREE.BoxGeometry(0.22, 0.09, 0.20), x: 0.44, y: 0.05, z: -0.34, ry: -0.9, c: char, t: 1 },
    // the ember channel: instanceColor drives emissive too, so a fresh wreck
    // glows hot and cools to charcoal without a second material.
    { g: new THREE.BoxGeometry(0.50, 0.05, 0.34), y: 0.31, c: [0.12, 0.05, 0.03], e: [1.0, 0.30, 0.06], ei: 1, t: 1 },
  ]);
}

export class EffectsLayer {
  /**
   * @param scene      THREE.Scene to add the pooled meshes to
   * @param world      the simulation, for entity lookups on events
   * @param heightAt   (x, y) -> terrain elevation in world units
   * @param player     whose fog gates ambient effects (defaults to the local player)
   */
  constructor(scene, world, heightAt, player = PLAYER) {
    this.world = world;
    this.scene = scene;
    this.player = player;
    this.heightAt = heightAt || (() => 0);
    this.rnd = makeRng(0x5eed1234);
    this._pendingSplash = 0;
    this._ambT = 0;
    this._ambPhase = 0;
    this._clock = 0;

    // --- tracers ----------------------------------------------------------
    this.tr = {
      x0: new Float32Array(TRACER_CAP), y0: new Float32Array(TRACER_CAP), z0: new Float32Array(TRACER_CAP),
      x1: new Float32Array(TRACER_CAP), y1: new Float32Array(TRACER_CAP), z1: new Float32Array(TRACER_CAP),
      life: new Float32Array(TRACER_CAP), max: new Float32Array(TRACER_CAP),
      w: new Float32Array(TRACER_CAP),
      r: new Float32Array(TRACER_CAP), g: new Float32Array(TRACER_CAP), b: new Float32Array(TRACER_CAP),
      cursor: 0,
    };
    const tg = new THREE.BufferGeometry();
    this._trPos = new Float32Array(TRACER_CAP * 18);   // 6 verts x vec3
    this._trCol = new Float32Array(TRACER_CAP * 18);
    this._trBeam = new Float32Array(TRACER_CAP * 12);  // 6 verts x vec2
    tg.setAttribute('position', new THREE.BufferAttribute(this._trPos, 3).setUsage(THREE.DynamicDrawUsage));
    tg.setAttribute('aCol', new THREE.BufferAttribute(this._trCol, 3).setUsage(THREE.DynamicDrawUsage));
    tg.setAttribute('aBeam', new THREE.BufferAttribute(this._trBeam, 2).setUsage(THREE.DynamicDrawUsage));
    tg.setDrawRange(0, 0);
    this.tracerMesh = new THREE.Mesh(tg, new THREE.ShaderMaterial({
      vertexShader: TRACER_VS, fragmentShader: TRACER_FS,
      transparent: true, depthWrite: false, blending: THREE.AdditiveBlending,
      side: THREE.DoubleSide,
    }));
    this.tracerMesh.frustumCulled = false;
    this.tracerMesh.renderOrder = 6;
    scene.add(this.tracerMesh);

    // --- particles --------------------------------------------------------
    const P = PARTICLE_CAP;
    this.pt = {
      kind: new Uint8Array(P),
      x: new Float32Array(P), y: new Float32Array(P), z: new Float32Array(P),
      sx: new Float32Array(P), sy: new Float32Array(P), sz: new Float32Array(P),
      tx: new Float32Array(P), ty: new Float32Array(P), tz: new Float32Array(P),
      vx: new Float32Array(P), vy: new Float32Array(P), vz: new Float32Array(P),
      life: new Float32Array(P), max: new Float32Array(P),
      s0: new Float32Array(P), s1: new Float32Array(P),
      arc: new Float32Array(P), splash: new Float32Array(P),
      trail: new Float32Array(P), spin: new Float32Array(P),
      r: new Float32Array(P), g: new Float32Array(P), b: new Float32Array(P),
      cursor: 0,
    };
    const quad = new THREE.PlaneGeometry(1, 1);
    this._ptParam = new Float32Array(P * 2);
    const partMat = new THREE.ShaderMaterial({
      vertexShader: PART_VS, fragmentShader: PART_FS,
      transparent: true, depthWrite: false, side: THREE.DoubleSide,
      blending: THREE.NormalBlending, premultipliedAlpha: true,
    });
    this.spriteMesh = new THREE.InstancedMesh(quad, partMat, P);
    this.spriteMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.spriteMesh.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(P * 3).fill(1), 3);
    this.spriteMesh.instanceColor.setUsage(THREE.DynamicDrawUsage);
    quad.setAttribute('aParam', new THREE.InstancedBufferAttribute(this._ptParam, 2).setUsage(THREE.DynamicDrawUsage));
    this.spriteMesh.count = 0;
    this.spriteMesh.frustumCulled = false;
    this.spriteMesh.renderOrder = 7;
    scene.add(this.spriteMesh);

    // --- ground rings -----------------------------------------------------
    this.rg = {
      x: new Float32Array(RING_CAP), y: new Float32Array(RING_CAP), z: new Float32Array(RING_CAP),
      life: new Float32Array(RING_CAP), max: new Float32Array(RING_CAP),
      r0: new Float32Array(RING_CAP), r1: new Float32Array(RING_CAP),
      r: new Float32Array(RING_CAP), g: new Float32Array(RING_CAP), b: new Float32Array(RING_CAP),
      cursor: 0,
    };
    const ringGeo = new THREE.RingGeometry(0.20, 1.0, 40);
    ringGeo.rotateX(-Math.PI / 2);
    this.ringMesh = new THREE.InstancedMesh(ringGeo, new THREE.ShaderMaterial({
      vertexShader: RING_VS, fragmentShader: RING_FS,
      transparent: true, depthWrite: false, blending: THREE.AdditiveBlending,
      side: THREE.DoubleSide,
    }), RING_CAP);
    this.ringMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.ringMesh.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(RING_CAP * 3).fill(1), 3);
    this.ringMesh.instanceColor.setUsage(THREE.DynamicDrawUsage);
    this.ringMesh.count = 0;
    this.ringMesh.frustumCulled = false;
    this.ringMesh.renderOrder = 5;
    scene.add(this.ringMesh);

    // --- wrecks -----------------------------------------------------------
    this.wr = {
      x: new Float32Array(WRECK_CAP), y: new Float32Array(WRECK_CAP), z: new Float32Array(WRECK_CAP),
      yaw: new Float32Array(WRECK_CAP), tip: new Float32Array(WRECK_CAP), scale: new Float32Array(WRECK_CAP),
      age: new Float32Array(WRECK_CAP), max: new Float32Array(WRECK_CAP),
      r: new Float32Array(WRECK_CAP), g: new Float32Array(WRECK_CAP), b: new Float32Array(WRECK_CAP),
      cursor: 0,
    };
    this.wreckMat = makeSolidMaterial({ roughness: 0.95, metalness: 0.0 });
    this.wreckMesh = new THREE.InstancedMesh(modelWreck(), this.wreckMat, WRECK_CAP);
    this.wreckMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.wreckMesh.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(WRECK_CAP * 3).fill(1), 3);
    this.wreckMesh.instanceColor.setUsage(THREE.DynamicDrawUsage);
    this.wreckMesh.count = 0;
    this.wreckMesh.frustumCulled = false;
    this.wreckMesh.castShadow = true;
    this.wreckMesh.receiveShadow = true;
    this.wreckMesh.renderOrder = 3;
    scene.add(this.wreckMesh);

    this.stats = { tracers: 0, particles: 0, rings: 0, wrecks: 0 };
  }

  setHeightSampler(fn) { if (fn) this.heightAt = fn; }
  setPlayer(p) { if (p === 0 || p === 1) this.player = p; }

  // ---- spawning ----------------------------------------------------------
  addTracer(x0, y0, z0, x1, y1, z1, life, r, g, b, width = 0.055) {
    const t = this.tr;
    const i = t.cursor; t.cursor = (i + 1) % TRACER_CAP;
    t.x0[i] = x0; t.y0[i] = y0; t.z0[i] = z0;
    t.x1[i] = x1; t.y1[i] = y1; t.z1[i] = z1;
    t.life[i] = life; t.max[i] = life; t.w[i] = width;
    t.r[i] = r; t.g[i] = g; t.b[i] = b;
  }

  /**
   * Next particle slot, oldest-first.
   *
   * A round in flight is the one thing that must never be evicted: recycling
   * its slot would delete the shell mid-air and its impact -- ring, plume, the
   * whole payoff -- would simply never happen. Under a heavy barrage the cursor
   * steps over live shells instead, so smoke degrades and artillery does not.
   */
  _slot() {
    const p = this.pt;
    let i = p.cursor;
    for (let guard = 0; guard < 8; guard++) {
      if (!(p.kind[i] === K_SHOT && p.life[i] > 0)) break;
      i = (i + 1) % PARTICLE_CAP;
    }
    p.cursor = (i + 1) % PARTICLE_CAP;
    p.trail[i] = 0; p.splash[i] = 0; p.arc[i] = 0; p.spin[i] = 0;
    p.vx[i] = 0; p.vy[i] = 0; p.vz[i] = 0;
    return i;
  }

  addFlash(x, y, z, size, life, r, g, b) {
    const p = this.pt, i = this._slot();
    p.kind[i] = K_FLASH;
    p.x[i] = x; p.y[i] = y; p.z[i] = z;
    p.life[i] = life; p.max[i] = life;
    p.s0[i] = size; p.s1[i] = size * 0.25;
    p.r[i] = r; p.g[i] = g; p.b[i] = b;
  }

  addSpark(x, y, z, vx, vy, vz, size, life, r, g, b) {
    const p = this.pt, i = this._slot();
    p.kind[i] = K_SPARK;
    p.x[i] = x; p.y[i] = y; p.z[i] = z;
    p.vx[i] = vx; p.vy[i] = vy; p.vz[i] = vz;
    p.life[i] = life; p.max[i] = life;
    p.s0[i] = size; p.s1[i] = size * 0.3;
    p.r[i] = r; p.g[i] = g; p.b[i] = b;
  }

  addSmoke(x, y, z, vx, vy, vz, size, life, r, g, b) {
    const p = this.pt, i = this._slot();
    p.kind[i] = K_SMOKE;
    p.x[i] = x; p.y[i] = y; p.z[i] = z;
    p.vx[i] = vx; p.vy[i] = vy; p.vz[i] = vz;
    p.life[i] = life; p.max[i] = life;
    p.s0[i] = size; p.s1[i] = size * 2.6;
    p.r[i] = r; p.g[i] = g; p.b[i] = b;
  }

  addDust(x, y, z, vx, vy, vz, size, life, r, g, b) {
    const p = this.pt, i = this._slot();
    p.kind[i] = K_DUST;
    p.x[i] = x; p.y[i] = y; p.z[i] = z;
    p.vx[i] = vx; p.vy[i] = vy; p.vz[i] = vz;
    p.life[i] = life; p.max[i] = life;
    p.s0[i] = size; p.s1[i] = size * 3.0;
    p.r[i] = r; p.g[i] = g; p.b[i] = b;
  }

  addDebris(x, y, z, vx, vy, vz, size, life) {
    const p = this.pt, i = this._slot();
    p.kind[i] = K_DEBRIS;
    p.x[i] = x; p.y[i] = y; p.z[i] = z;
    p.vx[i] = vx; p.vy[i] = vy; p.vz[i] = vz;
    p.life[i] = life; p.max[i] = life;
    p.s0[i] = size; p.s1[i] = size * 0.75;
    p.r[i] = 0.09; p.g[i] = 0.08; p.b[i] = 0.075;
  }

  /** Legacy alias kept for callers that just want "a puff of something". */
  addPuff(x, y, z, vx, vy, vz, size, life, r, g, b) {
    this.addSmoke(x, y, z, vx, vy, vz, size, life, r, g, b);
  }

  addShot(x0, y0, z0, x1, y1, z1, life, arc, splash, r, g, b, trail = 0) {
    const p = this.pt, i = this._slot();
    p.kind[i] = K_SHOT;
    p.sx[i] = x0; p.sy[i] = y0; p.sz[i] = z0;
    p.tx[i] = x1; p.ty[i] = y1; p.tz[i] = z1;
    p.x[i] = x0; p.y[i] = y0; p.z[i] = z0;
    p.life[i] = life; p.max[i] = life;
    p.s0[i] = 0.30; p.s1[i] = 0.30;
    p.arc[i] = arc; p.splash[i] = splash; p.trail[i] = trail;
    p.r[i] = r; p.g[i] = g; p.b[i] = b;
  }

  addRing(x, y, z, r0, r1, life, r, g, b) {
    const q = this.rg;
    const i = q.cursor; q.cursor = (i + 1) % RING_CAP;
    q.x[i] = x; q.y[i] = y; q.z[i] = z;
    q.r0[i] = r0; q.r1[i] = r1;
    q.life[i] = life; q.max[i] = life;
    q.r[i] = r; q.g[i] = g; q.b[i] = b;
  }

  addWreck(x, y, z, yaw, scale, owner) {
    const w = this.wr;
    const i = w.cursor; w.cursor = (i + 1) % WRECK_CAP;
    w.x[i] = x; w.y[i] = y; w.z[i] = z;
    w.yaw[i] = yaw;
    w.tip[i] = (this.rnd() < 0.5 ? -1 : 1) * (0.9 + this.rnd() * 0.5);
    w.scale[i] = scale;
    w.age[i] = 0; w.max[i] = 9.5;
    const t = owner >= 0 ? PALETTE.team[owner] : PALETTE.neutral;
    w.r[i] = 1.0 + t[0] * 0.3; w.g[i] = 1.0 + t[1] * 0.3; w.b[i] = 1.0 + t[2] * 0.3;
  }

  // ---- event ingestion ---------------------------------------------------
  /** Height of an entity's business end, for muzzles and impacts. */
  _entY(id, fx, fy) {
    const e = this.world.entities.get(id);
    const ground = this.heightAt(fx, fy);
    if (!e) return ground + 0.6;
    if (e.flying) return ground + FLY_ALT;
    if (e.kind === 'building') return ground + 0.9;
    return ground + 0.58;
  }

  /**
   * Where the shot actually leaves the model: the weapon, not the origin. A
   * tracer that starts at a rifleman's navel reads as a bug; one that starts at
   * the muzzle reads as a rifle.
   */
  _muzzle(id, fx, fy, out) {
    const e = this.world.entities.get(id);
    const ground = this.heightAt(fx, fy);
    out[0] = fx; out[1] = ground + 0.58; out[2] = fy;
    if (!e) return out;
    if (e.kind === 'building') {
      out[1] = ground + (e.defId === 'turret' ? 1.15 : 0.9);
      return out;
    }
    const m = MUZZLE[e.defId];
    if (!m) { out[1] = ground + (e.flying ? FLY_ALT : 0.58); return out; }
    const f = e.facing || 0;
    const c = Math.cos(f), s = Math.sin(f);
    out[0] = fx + m[0] * c - m[2] * s;
    out[2] = fy + m[0] * s + m[2] * c;
    out[1] = (e.flying ? ground + FLY_ALT : ground) + m[1];
    return out;
  }

  _teamTint(id, base) {
    const e = this.world.entities.get(id);
    const t = e && e.owner >= 0 ? PALETTE.team[e.owner] : null;
    if (!t) return base;
    _tint[0] = base[0] * 0.62 + t[0] * 0.62;
    _tint[1] = base[1] * 0.62 + t[1] * 0.62;
    _tint[2] = base[2] * 0.62 + t[2] * 0.62;
    return _tint;
  }

  /**
   * Consume one frame's worth of simulation events. The renderer drains
   * world.drainEvents() exactly once per frame and hands the array here.
   */
  consume(events) {
    if (!events || !events.length) return;
    for (let i = 0; i < events.length; i++) {
      const ev = events[i];
      switch (ev.type) {
        case 'attack': this._onAttack(ev); break;
        case 'splash': this._onSplash(ev); break;
        case 'death': this._onDeath(ev); break;
        case 'build_done': this._onBuildDone(ev); break;
        case 'sieged': this._onSiege(ev, true); break;
        case 'unsieged': this._onSiege(ev, false); break;
        default: break;
      }
    }
  }

  _onAttack(ev) {
    this._muzzle(ev.from, ev.x, ev.y, _mz);
    const mx = _mz[0], my = _mz[1], mz = _mz[2];
    const ty = this._entY(ev.to, ev.tx, ev.ty);
    const w = ev.weapon;
    const base = WEAPON_COLOR[w] || [1, 0.95, 0.8];
    const c = this._teamTint(ev.from, base);
    const cr = c[0], cg = c[1], cb = c[2];

    if (!w) {
      // melee: no tracer. A hard impact spark and two chips of debris instead.
      this.addFlash(ev.tx, ty, ev.ty, 0.52, 0.13, cr * 1.6, cg * 1.6, cb * 1.6);
      for (let k = 0; k < 2; k++) {
        const a = this.rnd() * Math.PI * 2;
        this.addSpark(ev.tx, ty, ev.ty, Math.cos(a) * 2.2, 1.4 + this.rnd(), Math.sin(a) * 2.2,
          0.13, 0.20, 1.0, 0.72, 0.34);
      }
      return;
    }

    if (w === 'bullet' || w === 'laser') {
      const laser = w === 'laser';
      const life = laser ? 0.14 : 0.075;
      this.addTracer(mx, my, mz, ev.tx, ty, ev.ty, life, cr, cg, cb, laser ? 0.075 : 0.05);
      this.addFlash(mx, my, mz, laser ? 0.34 : 0.28, 0.06, cr * 1.5, cg * 1.5, cb * 1.5);   // muzzle
      this.addFlash(ev.tx, ty, ev.ty, 0.36, 0.11, cr * 1.5, cg * 1.5, cb * 1.5);            // impact
      for (let k = 0; k < 2; k++) {
        const a = this.rnd() * Math.PI * 2;
        this.addSpark(ev.tx, ty, ev.ty, Math.cos(a) * 1.9, 0.9 + this.rnd() * 1.4, Math.sin(a) * 1.9,
          0.10, 0.16 + this.rnd() * 0.1, cr, cg * 0.9, cb * 0.7);
      }
      return;
    }

    // ballistic weapons get a travelling round so the tank's arc reads
    const dx = ev.tx - mx, dz = ev.ty - mz;
    const dist = Math.hypot(dx, dz);
    const isArc = w === 'arc';
    const speed = isArc ? 22 : w === 'shell' ? 42 : 28;
    const life = Math.max(0.08, Math.min(0.95, dist / speed));
    const arc = isArc ? Math.max(2.6, dist * 0.42) : w === 'missile' ? 0.6 : 0.18;
    const splash = ev.splash || 0;
    if (splash > 0) this._pendingSplash++;
    this.addShot(mx, my, mz, ev.tx, ty, ev.ty, life, arc, splash, cr, cg, cb, isArc ? 0.030 : 0.055);

    // Firing a siege gun is the loudest thing in the game. Make the muzzle
    // match: a wide flash, a blast ring of dust and a slug of smoke.
    if (isArc || w === 'shell') {
      const big = isArc ? 1.0 : 0.62;
      this.addFlash(mx, my, mz, 0.95 * big, 0.13, 1.0, 0.86, 0.52);
      this.addFlash(mx, my, mz, 1.8 * big, 0.08, 1.0, 0.62, 0.24);
      const f = Math.atan2(dz, dx);
      const fc = Math.cos(f), fs = Math.sin(f);
      for (let k = 0; k < (isArc ? 5 : 3); k++) {
        const spread = (this.rnd() - 0.5) * 1.1;
        this.addSmoke(
          mx + fc * 0.3, my, mz + fs * 0.3,
          fc * (3.4 + this.rnd() * 2) - fs * spread, 0.5 + this.rnd(), fs * (3.4 + this.rnd() * 2) + fc * spread,
          0.32 * big, 0.55, 0.42, 0.39, 0.35,
        );
      }
      if (isArc) {
        // NOTE: dust, not a ring. render.test.js asserts that firing a siege
        // shell spawns NO ring until the shell actually lands, and it is right
        // to -- a ring at the muzzle would read as an impact.
        const gy = this.heightAt(mx, mz) + 0.06;
        for (let k = 0; k < 5; k++) {
          const a = this.rnd() * Math.PI * 2;
          this.addDust(mx, gy, mz, Math.cos(a) * 3.4, 0.6, Math.sin(a) * 3.4,
            0.34, 0.55, DUST_COLOR[0], DUST_COLOR[1], DUST_COLOR[2]);
        }
      }
    } else {
      this.addFlash(mx, my, mz, 0.55, 0.09, cr * 1.4, cg * 1.4, cb * 1.4);
    }
  }

  /**
   * The sim applies splash damage the instant the shot is fired, but visually
   * the shell is still in the air. Rings for shots we are already animating are
   * spawned on impact instead; only splash from hitscan sources lands here.
   */
  _onSplash(ev) {
    if (this._pendingSplash > 0) { this._pendingSplash--; return; }
    const y = this.heightAt(ev.x, ev.y) + 0.08;
    this.addRing(ev.x, y, ev.y, ev.radius * 0.35, ev.radius * 1.6, 0.34, 1.0, 0.62, 0.26);
  }

  _onDeath(ev) {
    const ground = this.heightAt(ev.x, ev.y);
    if (ev.kind === 'resource') {
      // a mined-out patch crumbles, it does not detonate
      for (let i = 0; i < 4; i++) {
        const a = this.rnd() * Math.PI * 2;
        this.addDust(ev.x, ground + 0.1, ev.y, Math.cos(a) * 1.2, 0.5, Math.sin(a) * 1.2,
          0.4, 0.7, DUST_COLOR[0], DUST_COLOR[1], DUST_COLOR[2]);
      }
      return;
    }
    const team = ev.owner >= 0 ? PALETTE.team[ev.owner] : PALETTE.neutral;
    const big = ev.kind === 'building';
    // scale the whole explosion off the thing that died
    const def = big ? null : UnitDefs[ev.defId];
    const size = big ? 2.2 : Math.max(0.5, (def ? def.radius : 0.4) * 2.4);
    const y = ground + size * 0.35;

    this.addFlash(ev.x, y, ev.y, size * 1.8, big ? 0.30 : 0.16, 1.0, 0.80, 0.42);
    this.addFlash(ev.x, y, ev.y, size * 0.9, big ? 0.42 : 0.22, 1.0, 0.55, 0.20);

    const nSmoke = big ? 12 : 4;
    for (let i = 0; i < nSmoke; i++) {
      const a = this.rnd() * Math.PI * 2;
      const sp = (big ? 2.6 : 1.3) * (0.35 + this.rnd());
      this.addSmoke(
        ev.x + (this.rnd() - 0.5) * size, y + this.rnd() * size * 0.5, ev.y + (this.rnd() - 0.5) * size,
        Math.cos(a) * sp, 1.3 + this.rnd() * 2.0, Math.sin(a) * sp,
        size * (big ? 0.55 : 0.45), big ? 1.5 : 0.85,
        SMOKE_COLOR[0] + team[0] * 0.10, SMOKE_COLOR[1] + team[1] * 0.08, SMOKE_COLOR[2] + team[2] * 0.08,
      );
    }
    const nSpark = big ? 10 : 5;
    for (let i = 0; i < nSpark; i++) {
      const a = this.rnd() * Math.PI * 2;
      const sp = (big ? 6 : 3.4) * (0.4 + this.rnd());
      this.addSpark(ev.x, y, ev.y, Math.cos(a) * sp, 3.0 + this.rnd() * 4, Math.sin(a) * sp,
        size * 0.16, 0.4 + this.rnd() * 0.35, 1.0, 0.72 + this.rnd() * 0.2, 0.34);
    }
    const nDeb = big ? 8 : 3;
    for (let i = 0; i < nDeb; i++) {
      const a = this.rnd() * Math.PI * 2;
      const sp = (big ? 4.5 : 2.6) * (0.4 + this.rnd());
      this.addDebris(ev.x, y, ev.y, Math.cos(a) * sp, 2.6 + this.rnd() * 3.4, Math.sin(a) * sp,
        size * (0.16 + this.rnd() * 0.12), 0.9 + this.rnd() * 0.5);
    }
    for (let i = 0; i < (big ? 8 : 3); i++) {
      const a = this.rnd() * Math.PI * 2;
      this.addDust(ev.x, ground + 0.05, ev.y, Math.cos(a) * (big ? 4.5 : 2.2), 0.4, Math.sin(a) * (big ? 4.5 : 2.2),
        size * 0.45, big ? 1.1 : 0.6, DUST_COLOR[0], DUST_COLOR[1], DUST_COLOR[2]);
    }
    this.addRing(ev.x, ground + 0.07, ev.y, size * 0.3, size * (big ? 2.4 : 1.5), big ? 0.55 : 0.32, 1.0, 0.62, 0.30);

    // What is left behind. Buildings leave a much bigger field of rubble.
    if (ev.kind !== 'resource') {
      this.addWreck(ev.x, ground, ev.y, (ev.id * 2.399963) % 6.2831853, big ? 2.1 : size * 0.62, ev.owner);
    }
  }

  _onBuildDone(ev) {
    const g = this.heightAt(ev.x, ev.y);
    const team = ev.owner >= 0 ? PALETTE.team[ev.owner] : PALETTE.neutral;
    this.addRing(ev.x, g + 0.1, ev.y, 0.5, 3.6, 0.55, team[0] * 1.8, team[1] * 1.8, team[2] * 1.8);
    for (let i = 0; i < 6; i++) {
      const a = this.rnd() * Math.PI * 2;
      this.addDust(ev.x + Math.cos(a) * 1.4, g + 0.08, ev.y + Math.sin(a) * 1.4,
        Math.cos(a) * 1.6, 0.5, Math.sin(a) * 1.6, 0.5, 0.7, DUST_COLOR[0], DUST_COLOR[1], DUST_COLOR[2]);
    }
    this.addFlash(ev.x, g + 1.0, ev.y, 2.2, 0.35, team[0] * 1.4, team[1] * 1.4, team[2] * 1.4);
  }

  /** A tank planting or lifting its spades throws dirt in a ring. */
  _onSiege(ev, down) {
    const g = this.heightAt(ev.x, ev.y);
    this.addRing(ev.x, g + 0.06, ev.y, 0.5, 2.2, 0.45, 0.9, 0.78, 0.5);
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2 + this.rnd() * 0.4;
      const sp = down ? 2.6 : 1.8;
      this.addDust(ev.x + Math.cos(a) * 0.7, g + 0.05, ev.y + Math.sin(a) * 0.7,
        Math.cos(a) * sp, 0.7 + this.rnd() * 0.6, Math.sin(a) * sp,
        0.34, 0.65, DUST_COLOR[0], DUST_COLOR[1], DUST_COLOR[2]);
    }
  }

  // ---- per-frame ---------------------------------------------------------
  /** Advance every pool by `dt` seconds and rebuild the GPU buffers. */
  update(dt, camQuat) {
    if (!(dt > 0)) dt = 0;
    if (dt > 0.1) dt = 0.1;
    this._clock += dt;
    this._ambient(dt);
    this._stepTracers(dt, camQuat);
    this._stepParticles(dt, camQuat);
    this._stepRings(dt);
    this._stepWrecks(dt);
  }

  /** Fog state at a tile: 0 unexplored, 1 explored, 2 visible. */
  _vis(x, y) {
    const f = this.world.fog[this.player];
    if (!f) return FOG_VISIBLE;
    const g = this.world.grid;
    const xi = x | 0, yi = y | 0;
    if (xi < 0 || yi < 0 || xi >= g.w || yi >= g.h) return 0;
    return f.vis[yi * g.w + xi];
  }

  /**
   * Continuous, non-event effects: dust under moving ground units, smoke off
   * anything badly hurt, gas venting from open geysers.
   *
   * Runs at 14 Hz on a rotating third of the entity list with a hard per-pass
   * budget, so a 300-unit push costs the same as a 20-unit skirmish and can
   * never starve the pool of combat flashes.
   */
  _ambient(dt) {
    this._ambT += dt;
    if (this._ambT < 1 / 14) return;
    this._ambT = 0;
    const phase = this._ambPhase = (this._ambPhase + 1) % 3;

    let dustLeft = 9, smokeLeft = 6, gasLeft = 3;
    for (const e of this.world.entities.values()) {
      if (!e.alive) continue;
      if ((e.id + phase) % 3 !== 0) continue;

      if (e.kind === 'resource') {
        if (gasLeft <= 0 || e.defId !== 'geyser' || e.refineryId != null) continue;
        if (this._vis(e.x, e.y) !== FOG_VISIBLE) continue;
        gasLeft--;
        const g = this.heightAt(e.x, e.y);
        this.addSmoke(
          e.x + (this.rnd() - 0.5) * 0.5, g + 0.55, e.y + (this.rnd() - 0.5) * 0.5,
          (this.rnd() - 0.5) * 0.35, 0.85 + this.rnd() * 0.5, (this.rnd() - 0.5) * 0.35,
          0.34, 1.5, 0.10, 0.36, 0.20,
        );
        continue;
      }

      if (e.owner !== this.player && this._vis(e.x, e.y) !== FOG_VISIBLE) continue;

      // A structure part-way through construction is at 10 % hp by design; it
      // is being built, not burning, and must not smoke.
      const frac = e.constructing ? 1 : e.hp / e.maxHp;
      if (smokeLeft > 0 && frac < (e.kind === 'building' ? 0.5 : 0.35)) {
        smokeLeft--;
        const g = this.heightAt(e.x, e.y);
        const h = e.kind === 'building' ? 1.1 : (e.flying ? FLY_ALT : 0.5);
        const s = e.kind === 'building' ? 0.55 : 0.22;
        this.addSmoke(
          e.x + (this.rnd() - 0.5) * 0.4, g + h, e.y + (this.rnd() - 0.5) * 0.4,
          (this.rnd() - 0.5) * 0.5, 0.9 + this.rnd() * 0.7, (this.rnd() - 0.5) * 0.5,
          s, 1.3, 0.16, 0.15, 0.145,
        );
        // and a lick of fire when it is nearly dead
        if (frac < 0.18) {
          this.addFlash(e.x, g + h * 0.7, e.y, s * 1.6, 0.16, 1.0, 0.42, 0.12);
        }
      }

      if (dustLeft > 0 && e.kind === 'unit' && !e.flying && e.moveState === 'moving') {
        const sp = Math.hypot(e.vx || 0, e.vy || 0);
        if (sp > 0.6) {
          dustLeft--;
          const g = this.heightAt(e.x, e.y);
          const inv = -0.28 / (sp || 1);
          const r = UnitDefs[e.defId] ? UnitDefs[e.defId].radius : 0.35;
          this.addDust(
            e.x + e.vx * inv + (this.rnd() - 0.5) * 0.2, g + 0.05, e.y + e.vy * inv + (this.rnd() - 0.5) * 0.2,
            e.vx * -0.10, 0.28 + this.rnd() * 0.2, e.vy * -0.10,
            r * (e.defId === 'siege_tank' ? 1.5 : 0.85), 0.55,
            DUST_COLOR[0], DUST_COLOR[1], DUST_COLOR[2],
          );
        }
      }
    }
  }

  _stepTracers(dt, camQuat) {
    const t = this.tr, pos = this._trPos, col = this._trCol, beam = this._trBeam;
    // The beam quad has to face the camera without needing the camera's
    // position: cross(beam direction, camera forward) is the widening axis.
    _fwd.set(0, 0, -1);
    if (camQuat) _fwd.applyQuaternion(camQuat);
    let n = 0;
    for (let i = 0; i < TRACER_CAP; i++) {
      if (t.life[i] <= 0) continue;
      t.life[i] -= dt;
      if (t.life[i] <= 0) { t.life[i] = 0; continue; }
      const f = t.life[i] / t.max[i];
      _dir.set(t.x1[i] - t.x0[i], t.y1[i] - t.y0[i], t.z1[i] - t.z0[i]);
      const len = _dir.length();
      if (len < 1e-5) continue;
      _dir.multiplyScalar(1 / len);
      _side.crossVectors(_dir, _fwd);
      if (_side.lengthSq() < 1e-8) _side.set(0, 1, 0); else _side.normalize();
      const hw = t.w[i] * (0.55 + 0.45 * f);
      const sx = _side.x * hw, sy = _side.y * hw, sz = _side.z * hw;
      const ax = t.x0[i], ay = t.y0[i], az = t.z0[i];
      const bx = t.x1[i], by = t.y1[i], bz = t.z1[i];
      const o = n * 18, ob = n * 12;
      // two triangles: (a-, a+, b+) and (a-, b+, b-)
      _tq[0] = ax - sx; _tq[1] = ay - sy; _tq[2] = az - sz;
      _tq[3] = ax + sx; _tq[4] = ay + sy; _tq[5] = az + sz;
      _tq[6] = bx + sx; _tq[7] = by + sy; _tq[8] = bz + sz;
      _tq[9] = ax - sx; _tq[10] = ay - sy; _tq[11] = az - sz;
      _tq[12] = bx + sx; _tq[13] = by + sy; _tq[14] = bz + sz;
      _tq[15] = bx - sx; _tq[16] = by - sy; _tq[17] = bz - sz;
      pos.set(_tq, o);
      beam.set(BEAM_UV, ob);
      const r = t.r[i] * f, g = t.g[i] * f, b = t.b[i] * f;
      for (let k = 0; k < 6; k++) { col[o + k * 3] = r; col[o + k * 3 + 1] = g; col[o + k * 3 + 2] = b; }
      n++;
    }
    const geo = this.tracerMesh.geometry;
    geo.setDrawRange(0, n * 6);
    geo.attributes.position.needsUpdate = true;
    geo.attributes.aCol.needsUpdate = true;
    geo.attributes.aBeam.needsUpdate = true;
    this.tracerMesh.visible = n > 0;
    this.stats.tracers = n;
  }

  _stepParticles(dt, camQuat) {
    const p = this.pt;
    const mat = this.spriteMesh.instanceMatrix.array;
    const col = this.spriteMesh.instanceColor.array;
    const par = this._ptParam;
    let n = 0;
    for (let i = 0; i < PARTICLE_CAP; i++) {
      if (p.life[i] <= 0) continue;
      p.life[i] -= dt;
      if (p.life[i] <= 0) {
        p.life[i] = 0;
        if (p.kind[i] === K_SHOT) this._impact(i);
        continue;
      }
      const f = p.life[i] / p.max[i];        // 1 -> 0 over the lifetime
      const t = 1 - f;
      const kind = p.kind[i];
      let size, br, add;
      if (kind === K_SHOT) {
        p.x[i] = p.sx[i] + (p.tx[i] - p.sx[i]) * t;
        p.z[i] = p.sz[i] + (p.tz[i] - p.sz[i]) * t;
        p.y[i] = p.sy[i] + (p.ty[i] - p.sy[i]) * t + Math.sin(Math.PI * t) * p.arc[i];
        size = p.s0[i];
        br = 1; add = 1;
        if (p.trail[i] > 0) {
          p.spin[i] += dt;
          if (p.spin[i] >= p.trail[i]) {
            p.spin[i] = 0;
            this.addSmoke(p.x[i], p.y[i], p.z[i], 0, 0.35, 0, 0.14, 0.55, 0.26, 0.24, 0.23);
          }
        }
      } else if (kind === K_SPARK) {
        p.vy[i] -= 13 * dt;
        p.x[i] += p.vx[i] * dt; p.y[i] += p.vy[i] * dt; p.z[i] += p.vz[i] * dt;
        p.vx[i] *= 0.92; p.vz[i] *= 0.92;
        size = p.s0[i] + (p.s1[i] - p.s0[i]) * t;
        br = f * f; add = 1;
      } else if (kind === K_SMOKE) {
        p.vy[i] += 0.9 * dt;                  // buoyant
        p.x[i] += p.vx[i] * dt; p.y[i] += p.vy[i] * dt; p.z[i] += p.vz[i] * dt;
        p.vx[i] *= 0.90; p.vz[i] *= 0.90; p.vy[i] *= 0.97;
        size = p.s0[i] + (p.s1[i] - p.s0[i]) * t;
        br = Math.min(1, f * 2.2) * 0.85; add = 0;
      } else if (kind === K_DUST) {
        p.vy[i] -= 1.2 * dt;
        p.x[i] += p.vx[i] * dt; p.y[i] += p.vy[i] * dt; p.z[i] += p.vz[i] * dt;
        p.vx[i] *= 0.86; p.vz[i] *= 0.86;
        size = p.s0[i] + (p.s1[i] - p.s0[i]) * t;
        br = Math.min(1, f * 1.8) * 0.55; add = 0;
      } else if (kind === K_DEBRIS) {
        p.vy[i] -= 17 * dt;
        p.x[i] += p.vx[i] * dt; p.y[i] += p.vy[i] * dt; p.z[i] += p.vz[i] * dt;
        size = p.s0[i] + (p.s1[i] - p.s0[i]) * t;
        br = Math.min(1, f * 3.0); add = 0;
      } else {
        size = p.s0[i] + (p.s1[i] - p.s0[i]) * t;
        br = f * f; add = 1;
      }
      _v.set(p.x[i], p.y[i], p.z[i]);
      _s.set(size, size, size);
      _q.copy(camQuat);
      _m.compose(_v, _q, _s);
      _m.toArray(mat, n * 16);
      col[n * 3] = p.r[i];
      col[n * 3 + 1] = p.g[i];
      col[n * 3 + 2] = p.b[i];
      par[n * 2] = br;
      par[n * 2 + 1] = add;
      n++;
    }
    this.spriteMesh.count = n;
    this.spriteMesh.visible = n > 0;
    this.spriteMesh.instanceMatrix.needsUpdate = true;
    this.spriteMesh.instanceColor.needsUpdate = true;
    this.spriteMesh.geometry.attributes.aParam.needsUpdate = true;
    this.stats.particles = n;
  }

  /** A travelling round reached its target. */
  _impact(i) {
    const p = this.pt;
    const x = p.tx[i], y = p.ty[i], z = p.tz[i];
    const s = p.splash[i];
    const gy = this.heightAt(x, z) + 0.07;

    if (s > 0) {
      // A siege shell landing is the most dramatic moment in the game: a hard
      // white flash, a fast shockwave, a slower dust wall and a real plume.
      this.addFlash(x, y, z, s * 1.5, 0.10, 1.0, 0.95, 0.80);
      this.addFlash(x, y + s * 0.4, z, s * 2.4, 0.26, 1.0, 0.60, 0.24);
      this.addRing(x, gy, z, s * 0.25, s * 2.6, 0.30, 1.0, 0.80, 0.42);
      this.addRing(x, gy, z, s * 0.5, s * 3.6, 0.62, 0.55, 0.42, 0.26);
      for (let k = 0; k < 8; k++) {
        const a = this.rnd() * Math.PI * 2;
        const sp = s * (2.0 + this.rnd() * 2.0);
        this.addDust(x, gy, z, Math.cos(a) * sp, 0.8 + this.rnd(), Math.sin(a) * sp,
          s * 0.55, 0.9 + this.rnd() * 0.4, DUST_COLOR[0], DUST_COLOR[1], DUST_COLOR[2]);
      }
      for (let k = 0; k < 5; k++) {
        const a = this.rnd() * Math.PI * 2;
        this.addSmoke(x, y + this.rnd() * s, z, Math.cos(a) * s, 1.8 + this.rnd() * 1.6, Math.sin(a) * s,
          s * 0.7, 1.5, SMOKE_COLOR[0], SMOKE_COLOR[1], SMOKE_COLOR[2]);
      }
      for (let k = 0; k < 8; k++) {
        const a = this.rnd() * Math.PI * 2;
        const sp = s * (3 + this.rnd() * 3);
        this.addSpark(x, y, z, Math.cos(a) * sp, 4 + this.rnd() * 5, Math.sin(a) * sp,
          0.18, 0.42 + this.rnd() * 0.3, 1.0, 0.78, 0.36);
      }
      for (let k = 0; k < 4; k++) {
        const a = this.rnd() * Math.PI * 2;
        this.addDebris(x, y, z, Math.cos(a) * s * 2.4, 3 + this.rnd() * 3, Math.sin(a) * s * 2.4, 0.16, 1.0);
      }
      return;
    }

    this.addFlash(x, y, z, 0.68, 0.13, 1.0, 0.80, 0.44);
    this.addRing(x, gy, z, 0.2, 1.0, 0.20, 1.0, 0.70, 0.34);
    for (let k = 0; k < 4; k++) {
      const a = this.rnd() * Math.PI * 2;
      this.addSpark(x, y, z, Math.cos(a) * 2.6, 1.8 + this.rnd() * 2, Math.sin(a) * 2.6,
        0.12, 0.25, 1.0, 0.76, 0.36);
    }
    this.addSmoke(x, y, z, 0, 0.7, 0, 0.30, 0.6, SMOKE_COLOR[0], SMOKE_COLOR[1], SMOKE_COLOR[2]);
  }

  _stepRings(dt) {
    const q = this.rg;
    const mat = this.ringMesh.instanceMatrix.array;
    const col = this.ringMesh.instanceColor.array;
    let n = 0;
    for (let i = 0; i < RING_CAP; i++) {
      if (q.life[i] <= 0) continue;
      q.life[i] -= dt;
      if (q.life[i] <= 0) { q.life[i] = 0; continue; }
      const f = q.life[i] / q.max[i];
      const t = 1 - f;
      // ease-out: the shockwave leaves fast and settles, like a real one
      const e = 1 - (1 - t) * (1 - t);
      const r = q.r0[i] + (q.r1[i] - q.r0[i]) * e;
      _v.set(q.x[i], q.y[i], q.z[i]);
      _s.set(r, 1, r);
      _q.identity();
      _m.compose(_v, _q, _s);
      _m.toArray(mat, n * 16);
      col[n * 3] = q.r[i] * f;
      col[n * 3 + 1] = q.g[i] * f;
      col[n * 3 + 2] = q.b[i] * f;
      n++;
    }
    this.ringMesh.count = n;
    this.ringMesh.visible = n > 0;
    this.ringMesh.instanceMatrix.needsUpdate = true;
    this.ringMesh.instanceColor.needsUpdate = true;
    this.stats.rings = n;
  }

  /**
   * Wrecks tip over on the way down, sit and smoulder, then sink and darken
   * out. Fading by darkening rather than by alpha keeps them opaque, so they
   * never sort badly against the terrain or each other.
   */
  _stepWrecks(dt) {
    const w = this.wr;
    const mat = this.wreckMesh.instanceMatrix.array;
    const col = this.wreckMesh.instanceColor.array;
    let n = 0;
    for (let i = 0; i < WRECK_CAP; i++) {
      if (w.max[i] <= 0) continue;
      w.age[i] += dt;
      const age = w.age[i];
      if (age >= w.max[i]) { w.max[i] = 0; continue; }
      const fall = Math.min(1, age / 0.55);
      const tip = w.tip[i] * (1 - (1 - fall) * (1 - fall));   // ease-out topple
      const gone = Math.max(0, (age - (w.max[i] - 2.2)) / 2.2);
      const s = w.scale[i];
      _v.set(w.x[i], w.y[i] - gone * 0.55 * s, w.z[i]);
      _e.set(tip * 0.55, w.yaw[i], tip * 0.45, 'YZX');
      _q.setFromEuler(_e);
      _s.set(s, s, s);
      _m.compose(_v, _q, _s);
      _m.toArray(mat, n * 16);
      // hot for the first second, then charcoal, then out
      const ember = Math.max(0, 1 - age * 0.7);
      const k = (1 - gone) * (0.45 + 0.55 * ember);
      col[n * 3] = w.r[i] * k;
      col[n * 3 + 1] = w.g[i] * k * (0.55 + 0.45 * (1 - ember));
      col[n * 3 + 2] = w.b[i] * k * (0.5 + 0.5 * (1 - ember));
      n++;
    }
    this.wreckMesh.count = n;
    this.wreckMesh.visible = n > 0;
    this.wreckMesh.instanceMatrix.needsUpdate = true;
    this.wreckMesh.instanceColor.needsUpdate = true;
    this.stats.wrecks = n;
  }

  dispose() {
    for (const m of [this.tracerMesh, this.spriteMesh, this.ringMesh, this.wreckMesh]) {
      this.scene.remove(m);
      m.geometry.dispose();
      m.material.dispose();
    }
  }
}

